<template>
  <div class="mt-3" v-if="items && items.length && !loading">
    <table class="table w-100">
      <tr class="bg-gray-100 text-dark">
        <th class="p-1"></th>
        <th class="p-1">{{ $t("models.productproducer.name") }}</th>
        <th class="p-1">{{ $t("models.productproducer.producer") }}</th>
        <th class="p-1">{{ $t("models.productproducer.price") }}</th>
        <th class="p-1">{{ $t("models.cart.qta") }}</th>
        <th class="p-1 text-right">{{ $t("models.cart.total") }}</th>
        <th class="p-1 text-right"></th>
      </tr>
      <MarketCartTableRow
        v-for="item in items"
        :key="item.id"
        :item="item"
        :market="market"
      />
    </table>
    <div class="text-right" v-if="cart">
      <form :action="checkoutActionUrl" method="post">
        <input type="hidden" name="token" :value="token" />
        <input type="hidden" name="redirect_url" :value="redirect_url" />
        <input type="hidden" name="carts_url" :value="carts_url" />
        <input type="hidden" name="market_url" :value="market_url" />
        <CButton color="primary" type="submit" size="lg">
          {{ $t("family.market.cart.proceed") }}
        </CButton>
      </form>
    </div>
  </div>
  <div v-else>
    <div class="text-center mt-4" v-if="loading">
      <CSpinner color="primary" size="lg" />
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import { getPriceUnitOptions, getPwOptions } from "../../../helpers/options";
import {
  addOrReplace,
  formatPrice,
  getOptionLabel,
  getTranslation,
} from "../../../helpers/common";
import get from "lodash/get";
import { apiGet } from "../../../helpers/apiHelper";
import MarketCartTableRow from "./MarketCartTableRow.vue";

export default {
  name: "MarketCartTable",

  props: {
    market: { type: Object, required: true },
    bus: { type: Object },
  },

  components: {
    MarketCartTableRow,
  },

  computed: {
    ...mapGetters("connections", ["familyId", "currencySymbol"]),

    lang: function () {
      return this.$i18n.locale;
    },

    checkoutActionUrl() {
      return this.cart ? this.cart.CheckoutUrl : "";
    },

    token() {
      return this.$auth.token();
    },

    redirect_url() {
      const currentUrl = new URL(window.location);
      const ordersRoute = this.$router.resolve({
        name: "UserMarketOrders",
        params: { familyId: this.familyId },
      });
      return `${currentUrl.origin}${ordersRoute.href}`;
    },

    carts_url() {
      const currentUrl = new URL(window.location);
      const cartsRoute = this.$router.resolve({
        name: "UserMarketCarts",
        params: { familyId: this.familyId },
      });
      return `${currentUrl.origin}${cartsRoute.href}`;
    },

    market_url() {
      const currentUrl = new URL(window.location);
      const marketRoute = this.$router.resolve({
        name: "UserMarketDetail",
        params: { familyId: this.familyId, marketId: this.market.market.id },
      });
      return `${currentUrl.origin}${marketRoute.href}`;
    },
  },

  async mounted() {
    await this.loadCart();
    this.$root.$on("onCartItemRemove", this.onCartItemRemove);
    this.$root.$on("onCartItemChangeQuantity", this.onCartItemChangeQuantity);
    this.$root.$on("onCartItemUpdate", this.onCartItemUpdate);
  },

  beforeDestroy() {
    this.$root.$off("onCartItemRemove", this.onCartItemRemove);
    this.$root.$off("onCartItemChangeQuantity", this.onCartItemChangeQuantity);
    this.$root.$off("onCartItemUpdate", this.onCartItemUpdate);
  },

  data: function () {
    return {
      cart: undefined,
      items: [],
      cartChanges: [],
      cartUpdating: false,
      loading: false,
    };
  },

  methods: {
    async loadCart(loadingVisual = true) {
      this.loading = loadingVisual;
      const familyId = this.familyId;
      const marketId = this.market.market.id;

      const response = await apiGet("family_market_current_cart", [
        familyId,
        marketId,
      ]);
      if (response && response.status === 200) {
        this.cart = response.data.payload;
        const sortedItems = this.sortItems(response.data.payload.items);
        this.items = sortedItems;
        this.loading = false;
      } else {
        this.loading = false;
        this.$store.dispatch("toaster/add", {
          title: this.$t("role.finance.invoicesSettings.attention"),
          text: "Error loading cart items",
          color: "danger",
          autohide: true,
        });
        this.loading = false;
      }
    },

    sortItems(items) {
      return items.sort((a, b) => {
        const nameA = get(
          a,
          "view.mp.ppl.productproducer.producer.role.name",
          ""
        ).toLowerCase();
        const nameB = get(
          b,
          "view.mp.ppl.productproducer.producer.role.name",
          ""
        ).toLowerCase();

        // First sort by producer name
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;

        // If names are equal, sort by id
        const idA = get(a, "id", 0);
        const idB = get(b, "id", 0);
        return idA - idB;
      });
    },

    cartTotal() {
      return formatPrice(this.cart.view.tot, this.currencySymbol).format();
    },

    updateCart(cart) {
      const self = this;
      const marketId = cart.market;
      this.cartUpdating = true;
      this.$store
        .dispatch("shop/setCartQuantity", {
          marketId,
          items: [...this.cartChanges],
        })
        .then(async () => {
          self.cartChanges = [];
          self.cartUpdating = false;
          await self.loadCart();
          self.$store.dispatch("toaster/add", {
            title: "OK!",
            text: this.$t("family.market.cart.msg_update"),
            color: "success",
            autohide: true,
          });
        });
    },

    onCartItemRemove({ row, data }) {
      const self = this;

      const ppm_id = row.ppm;
      const quantity = 0;
      const marketId = data.marketId;

      if (this.market.market.id === marketId) {
        this.$store
          .dispatch("shop/setCartQuantity", {
            marketId,
            items: [{ ppm_id, quantity }],
          })
          .then(async () => {
            await self.loadCart();
            self.$emit("cartUpdated", self.cart);
            self.$store.dispatch("toaster/add", {
              title: "OK!",
              text: this.$t("family.market.cart.msg_remove"),
              color: "success",
              autohide: true,
            });
          });
      }
    },

    onCartItemUpdate({ row, data }) {
      const self = this;

      const ppm_id = row.ppm;
      const quantity = data.quantity;
      const marketId = data.marketId;

      if (this.market.market.id === marketId) {
        this.$store
          .dispatch("shop/setCartQuantity", {
            marketId,
            items: [{ ppm_id, quantity }],
          })
          .then(async () => {
            await self.loadCart(false);
            self.$emit("cartUpdated", self.cart);
            self.$store.dispatch("toaster/add", {
              title: "OK!",
              text: this.$t("family.market.cart.msg_qtaupdt"),
              color: "success",
              autohide: true,
            });
          });
      }
    },

    onCartItemChangeQuantity({ row, data }) {
      const ppm_id = row.ppm;
      const { marketId, quantity } = data;
      if (this.market.market.id === marketId) {
        this.cartChanges = addOrReplace(
          this.cartChanges,
          {
            ppm_id,
            quantity,
          },
          "ppm_id"
        );
      }
    },
  },
};
</script>
